import {
  allAnimalVariablesExcluded,
  formatAverage,
  getExcludedIncludedMap,
  getExcludedOrIncludedVariableApiIds,
  getVariableApiIdsFromMetrics,
  type AnimalValues,
  type GroupExtendedWithMetrics,
  type GroupMetricAnimal,
} from '@/components/Studies/Groups/Groups.utils';
import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import IncludeExcludeVariablesButton from '@/components/UI/IncludeExcludeVariablesButton';
import Link from '@/components/UI/Link';
import Button from '@/components/UI/Button/Button';
import { formatNumber, objectDiff, successToast } from '@/helpers';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { VariableApiId } from '@/model/Measurement.model';
import type { Study } from '@/model/Study.model';
import * as Auth from '@/support/auth';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { web as webRoute } from '@/support/route';
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import cn from 'classnames';
import { type FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form@latest';
import './GroupsTable.scss';

type ExcludedMap = Record<VariableApiId, boolean>;

interface GroupTableProps {
  metrics: Array<GroupExtendedWithMetrics>;
  group: GroupExtendedWithMetrics;
  study: Study;
  initialValues: ExcludedMap;
}

interface AnimalCellProps {
  studyId: ID;
  animalId: ID;
  animalName: string;
  animalVariableApiIds: Array<VariableApiId>;
  isWriteUser?: boolean;
}

interface DataCellProps {
  value: string | null;
  variableApiId: VariableApiId;
  isWriteUser?: boolean;
  initialValues: ExcludedMap;
}

interface TableButtonActionsProps {
  onSubmit: (formData: ExcludedMap) => Promise<void>;
  onReset: () => Promise<void>;
  isBusy: boolean;
  initialValues: ExcludedMap;
}

interface ColumnMeta {
  isFixed?: boolean;
  className?: string;
}

interface GroupsTablesProps {
  metrics: Array<GroupExtendedWithMetrics>;
  study: Study;
  fetchMetrics: () => Promise<void>;
}

const TABLE_FOOTER_CLASSES = 'db fw3 near-black lh-copy';

const GroupsTables: FC<GroupsTablesProps> = ({ metrics, study, fetchMetrics }) => {
  const initExcludedMap = useMemo(() => getExcludedIncludedMap(metrics), [metrics]);
  const formMethods = useForm<ExcludedMap>({ defaultValues: initExcludedMap });
  const { reset } = formMethods;
  const { invoke: includeVariables, loading: includeVariablesUpdating } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyId}/variables-include',
    invokeOnInit: false,
  });
  const { invoke: excludeVariables, loading: excludeVariablesUpdating } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyId}/variables-exclude',
    invokeOnInit: false,
  });

  useEffect(() => {
    reset(getExcludedIncludedMap(metrics));
  }, [metrics, reset]);

  const onResetVariables = async (): Promise<void> => {
    const includes: Array<VariableApiId> = [];
    for (const variableApiId in initExcludedMap) {
      if (initExcludedMap[variableApiId as VariableApiId] === true) {
        includes.push(variableApiId as VariableApiId);
      }
    }
    if (_isNotEmpty(includes)) {
      await includeVariables({
        path: { studyId: study.api_id },
        body: { variable_api_ids: includes },
      });
      await fetchMetrics();
      successToast('Reset excluded measurements');
    }
  };

  const onSubmitChanges = async (formData: ExcludedMap): Promise<void> => {
    const formDiffs = objectDiff(initExcludedMap, formData);
    const excludes: Array<VariableApiId> = [];
    const includes: Array<VariableApiId> = [];
    for (const variableApiId in formDiffs) {
      const diff = formDiffs[variableApiId]?.obj2;
      if (diff === true) {
        excludes.push(variableApiId as VariableApiId);
      }
      if (diff === false) {
        includes.push(variableApiId as VariableApiId);
      }
    }
    if (_isNotEmpty(excludes)) {
      await excludeVariables({
        path: { studyId: study.api_id },
        body: { variable_api_ids: excludes },
      });
      await fetchMetrics();
    }
    if (_isNotEmpty(includes)) {
      await includeVariables({
        path: { studyId: study.api_id },
        body: { variable_api_ids: includes },
      });
      await fetchMetrics();
    }
    successToast('Updated measurements');
  };

  return (
    <div>
      <FormProvider {...formMethods}>
        {Auth.isWriteUserForStudy(study) && (
          <TableButtonActions
            onSubmit={onSubmitChanges}
            onReset={onResetVariables}
            isBusy={includeVariablesUpdating || excludeVariablesUpdating}
            initialValues={initExcludedMap}
          />
        )}
        {metrics.map((group) => (
          <div key={group.api_id} className="bt odd:b--moon-gray" data-testid={`graphs__tables--entry:${group.id}`}>
            <GroupTable
              key={`group-table-${group.id}`}
              metrics={metrics}
              group={group}
              study={study}
              initialValues={initExcludedMap}
            />
          </div>
        ))}
      </FormProvider>
    </div>
  );
};

const TableButtonActions: FC<TableButtonActionsProps> = ({ onSubmit, onReset, isBusy, initialValues }) => {
  const { handleSubmit, control } = useFormContext<ExcludedMap>();
  const currentValues = useWatch({ control });
  const diffs = objectDiff(initialValues, currentValues as ExcludedMap);
  const notTouched = _isEmpty(diffs);
  const hasNoInitialExcluded = _isEmpty(getExcludedOrIncludedVariableApiIds(initialValues, true));

  return (
    <div className="flex justify-end pa3 bt b--moon-gray">
      <Button plain onClick={() => onReset()} disabled={isBusy || hasNoInitialExcluded}>
        Reset
      </Button>
      <Button className="ml3" onClick={handleSubmit(onSubmit)} disabled={isBusy || notTouched}>
        Submit changes
      </Button>
    </div>
  );
};

const GroupTable: FC<GroupTableProps> = ({ metrics, group, study, initialValues }) => {
  const isWriteUser = Auth.isWriteUserForStudy(study);
  const averageTypes = ['sd', 'sem', 'mad'];

  const dayColumns = metrics
    .reduce<Array<number>>((days, currentGroup) => {
      if (_isNotEmpty(currentGroup.animals)) {
        currentGroup.animals.forEach((animal) => {
          if (_isNotEmpty(animal.values)) {
            animal.values.forEach(({ study_day: studyDay }) => {
              if (!days.includes(studyDay)) {
                days.push(studyDay);
              }
            });
          }
        });
      }
      return days;
    }, [])
    .sort((a, b) => Number(a) - Number(b))
    .map<ColumnDef<GroupMetricAnimal, AnimalValues[number] | undefined>>((day) => {
      const averages = group.averages.find((average) => average.study_day === day);

      const metricValue = (method: string, metrics: number, spaceTop: boolean) => (
        <span key={method} className={cn(TABLE_FOOTER_CLASSES, method, { mt2: spaceTop })}>
          {averageTypes.includes(method) ? '±' : ''}
          {formatAverage(metrics)}
        </span>
      );

      return {
        id: `day-${day}`,
        header: () => `Day ${day}`,
        accessorFn: (animal) => animal?.values?.find((a) => a.study_day === day),
        cell: ({ getValue }) => {
          const variable = getValue();
          const apiId = variable?.api_id;
          const value = _notNil(variable) && _notNil(variable.value) ? String(formatNumber(variable.value)) : null;
          if (_notNil(value) && _notNil(apiId)) {
            return (
              <DataCell value={value} variableApiId={apiId} isWriteUser={isWriteUser} initialValues={initialValues} />
            );
          }
          return <>{value}</>;
        },
        footer: () =>
          _notNil(averages) ? (
            <>
              {metricValue('mean', averages.mean, false)}
              {metricValue('sd', averages.sd, false)}
              {metricValue('sem', averages.sem, false)}
              {metricValue('median', averages.median, true)}
              {metricValue('mad', averages.mad, false)}
              {metricValue('dtdc', averages.dtdc, true)}
              {metricValue('tgi', averages.tgi, false)}
            </>
          ) : null,
        meta: { className: 'ui__day-column' } satisfies ColumnMeta,
      };
    });

  const columns: Array<ColumnDef<GroupMetricAnimal>> = [
    {
      id: 'group-label',
      accessorKey: 'group.name',
      header: () => <GroupLabel group={group} />,
      footer: () => (
        <div>
          <span className={`${TABLE_FOOTER_CLASSES} mean`}>Mean</span>
          <span className={`${TABLE_FOOTER_CLASSES} sd`}>SD</span>
          <span className={`${TABLE_FOOTER_CLASSES} sem`}>SEM</span>
          <span className={`${TABLE_FOOTER_CLASSES} mt2 median`}>Median</span>
          <span className={`${TABLE_FOOTER_CLASSES} mad`}>MAD</span>
          <span className={`${TABLE_FOOTER_CLASSES} mt2 dtdc`}>% dT/dC</span>
          <span className={`${TABLE_FOOTER_CLASSES} tgi`}>% Inhibition</span>
        </div>
      ),
      size: 175,
      meta: { isFixed: true, className: 'ui__fixed-column ui__group-column' } satisfies ColumnMeta,
    },
    {
      id: 'number',
      accessorKey: 'number',
      header: () => 'No.',
      size: 50,
      meta: { isFixed: true, className: 'ui__fixed-column ui__number-column' } satisfies ColumnMeta,
    },
    {
      id: 'animal-name',
      accessorKey: 'name',
      header: () => 'ID',
      size: 150,
      cell: ({ row }) => {
        const animalId = row.original?.id;
        const animalName = row.original?.name ?? '';
        const animalVariableApiIds = getVariableApiIdsFromMetrics(animalId, metrics);
        if (_isNil(animalId)) {
          return <div>-</div>;
        }
        return (
          <AnimalCell
            studyId={study.id}
            animalId={animalId}
            animalName={animalName}
            animalVariableApiIds={animalVariableApiIds}
            isWriteUser={isWriteUser}
          />
        );
      },
      meta: { isFixed: true, className: 'ui__fixed-column ui__animal-column' } satisfies ColumnMeta,
    },
    ...(dayColumns as Array<ColumnDef<GroupMetricAnimal>>),
  ];

  const table = useReactTable<GroupMetricAnimal>({
    data: group.animals ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    enableColumnResizing: false,
    enableFilters: false,
  });

  const noData = _isEmpty(group.animals);

  return (
    <div className="ui__groups-table -striped" key={group.name}>
      <div className="rt-thead -header">
        {table.getHeaderGroups().map((headerGroup) => (
          <div key={headerGroup.id} className="rt-tr">
            {headerGroup.headers.map((header) => {
              const meta = header.column.columnDef.meta as ColumnMeta | undefined;
              return (
                <div
                  key={header.id}
                  className={cn('rt-th', meta?.className)}
                  style={{
                    width: header.getSize(),
                    minWidth: header.getSize(),
                    maxWidth: meta?.isFixed === true ? header.getSize() : undefined,
                  }}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="rt-tbody">
        {noData ? (
          <div className="rt-tr-group">
            <div className="rt-tr -odd">
              <div className="rt-td" style={{ textAlign: 'center', padding: '20px' }}>
                No measurements yet
              </div>
            </div>
          </div>
        ) : (
          table.getRowModel().rows.map((row, index) => (
            <div key={row.id} className="rt-tr-group">
              <div className={cn('rt-tr', index % 2 === 0 ? '-even' : '-odd')}>
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta as ColumnMeta | undefined;
                  return (
                    <div
                      key={cell.id}
                      className={cn('rt-td', meta?.className)}
                      style={{
                        width: cell.column.getSize(),
                        minWidth: cell.column.getSize(),
                        maxWidth: meta?.isFixed === true ? cell.column.getSize() : undefined,
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="rt-tfoot">
        {table.getFooterGroups().map((footerGroup) => (
          <div key={footerGroup.id} className="rt-tr">
            {footerGroup.headers.map((header) => {
              const meta = header.column.columnDef.meta as ColumnMeta | undefined;
              return (
                <div
                  key={header.id}
                  className={cn('rt-td', meta?.className)}
                  style={{
                    width: header.getSize(),
                    minWidth: header.getSize(),
                    maxWidth: meta?.isFixed === true ? header.getSize() : undefined,
                  }}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const AnimalCell: FC<AnimalCellProps> = ({
  studyId,
  animalId,
  animalName,
  animalVariableApiIds,
  isWriteUser = false,
}) => {
  const { setValue } = useFormContext<ExcludedMap>();
  // useWatch with an array of names returns an array; zip back to Record<api_id, boolean>
  // so the toggle below iterates by real `vrb_xxx` paths instead of array indices.
  const watched = useWatch({ name: animalVariableApiIds }) as Array<boolean | undefined>;
  const variableState = useMemo<ExcludedMap>(() => {
    const map = {} as ExcludedMap;
    animalVariableApiIds.forEach((id, index) => {
      map[id] = watched[index] === true;
    });
    return map;
  }, [animalVariableApiIds, watched]);
  const allVariablesExcluded = allAnimalVariablesExcluded(variableState);

  const handleIncludeExcludeClick = (): void => {
    const toExclude = !allVariablesExcluded;
    for (const variableKey in variableState) {
      setValue(variableKey as VariableApiId, toExclude);
    }
  };

  return (
    <div className="w-100 hide-child">
      <div className="flex justify-between items-center relative">
        <Link to={webRoute('animals.show', { id: studyId, subject_id: animalId })} className="link blue">
          {animalName}
        </Link>
        {isWriteUser && (
          <div className="include-exclude-variable-button child absolute right-0 z-2">
            <IncludeExcludeVariablesButton isExcluded={allVariablesExcluded} onClick={handleIncludeExcludeClick} />
          </div>
        )}
      </div>
    </div>
  );
};

// `register(name)` inside `useEffect` is the documented v7 pattern for a "virtual input":
// no DOM ref is bound, the field is read via `useWatch` and updated via `setValue`.
const DataCell: FC<DataCellProps> = ({ value, variableApiId, isWriteUser = false, initialValues }) => {
  const { register, setValue } = useFormContext<ExcludedMap>();
  const excluded = useWatch({ name: variableApiId }) as boolean | undefined;
  const initialExcluded = initialValues?.[variableApiId] === true;

  useEffect(() => {
    register(variableApiId);
  }, [register, variableApiId]);

  const handleClick = (): void => {
    if (isWriteUser) {
      setValue(variableApiId, excluded !== true);
    }
  };

  const isExcluded = excluded === true;
  const className = cn({
    pointer: isWriteUser,
    blue: initialExcluded,
    strike: isExcluded || (initialExcluded && isExcluded),
    'strike-hover': !isExcluded || !initialExcluded,
  });

  return (
    <div
      onClick={handleClick}
      className={className}
      data-test-component="DataCell"
      data-test-element="cell"
      data-test-key={variableApiId}
    >
      {value}
    </div>
  );
};

export default GroupsTables;
